import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import TextInput, { TextInputProps } from "./TextInput";
import {
  DROPDOWN_YEAR_RANGE,
  FIRST_MONTH_OF_YEAR,
  LAST_MONTH_OF_YEAR,
} from "../../constants/common";
import { cn } from "../../utils/cn";
import { memo, useState } from "react";

interface DateTimePickerInputProps extends Omit<
  TextInputProps,
  "onChange" | "value" | "name"
> {
  label: string;
  readOnly?: boolean;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  onChange?: (date: Date | null) => void;
  highlightBorderRed?: boolean;
  value: any;
  error?: string | undefined;
  dateTimeValidation?: (date: Date) => boolean;
}

const DateTimePickerInput = ({
  value,
  label,
  readOnly = false,
  required = false,
  minDate,
  maxDate,
  onChange: customOnChange,
  highlightBorderRed = false,
  error,
  dateTimeValidation,
  isInfoToolTip,
  infoToolTip,
  ...props
}: DateTimePickerInputProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [month, setMonth] = useState<Date>(value ?? new Date());

  const handleDateChange = (date: Date | undefined) => {
    if (!date) return;

    const currentDate = value ? new Date(value) : new Date();
    const hours = currentDate.getHours();
    const minutes = currentDate.getMinutes();
    const newDate = new Date(date);
    newDate.setHours(hours);
    newDate.setMinutes(minutes);

    if (customOnChange) customOnChange(newDate);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value;

    // Only process if we have a complete time format (HH:MM)
    if (!time || !time.includes(":") || time.split(":").length !== 2) {
      return;
    }

    const [hoursStr, minutesStr] = time.split(":");
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    // Validate that hours and minutes are valid numbers
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return;
    }

    const newDate = value ? new Date(value) : new Date();
    newDate?.setHours(hours);
    newDate?.setMinutes(minutes);

    let timeUpdate = true;
    const isTimeUpdate = dateTimeValidation?.(newDate) ?? true;
    timeUpdate = isTimeUpdate;

    if (timeUpdate) if (customOnChange) customOnChange(newDate);
  };

  return (
    <div className="w-full">
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (!readOnly) {
            if (open && value) {
              setMonth(value);
            }
            setIsOpen(open);
          }
        }}
      >
        <PopoverTrigger asChild>
          <div className="relative w-full">
            <TextInput
              label={label}
              disabled={readOnly}
              required={required}
              value={
                value
                  ? format(
                      value,
                      i18n.language === "en"
                        ? "dd/MM/yyyy, hh:mm a"
                        : "dd/MM/yyyy ،hh:mm a",
                    )
                  : ""
              }
              error={error as string}
              className={cn(
                "w-full cursor-pointer",
                highlightBorderRed && "border-red-600",
                readOnly && "cursor-not-allowed bg-[#f0f0f0]",
              )}
              isInfoToolTip={isInfoToolTip}
              infoToolTip={infoToolTip}
              {...props}
            />
            <CalendarIcon
              className={cn(
                "absolute bottom-2 -translate-y-1/2 h-4 w-4",
                i18n.language === "en" ? "right-3" : "left-3",
                readOnly
                  ? "text-gray-400 cursor-not-allowed"
                  : "cursor-pointer",
              )}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-full sm:w-auto p-2 bg-white" align="start">
          <DayPicker
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
            mode="single"
            selected={value ?? undefined}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleDateChange}
            disabled={
              minDate && maxDate
                ? { before: minDate, after: maxDate }
                : minDate
                  ? { before: minDate }
                  : maxDate
                    ? { after: maxDate }
                    : undefined
            }
            captionLayout="dropdown"
            startMonth={
              new Date(
                new Date().getFullYear() - DROPDOWN_YEAR_RANGE,
                FIRST_MONTH_OF_YEAR,
              )
            }
            endMonth={
              new Date(
                new Date().getFullYear() + DROPDOWN_YEAR_RANGE,
                LAST_MONTH_OF_YEAR,
              )
            }
            animate={true}
            className="w-full sm:w-auto"
          />
          <div className="p-3 border-t border-gray-200">
            <input
              type="time"
              value={value ? format(value, "HH:mm") : ""}
              onKeyDown={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                if (readOnly) return;

                const timeInput = e.target as HTMLInputElement;
                if (timeInput.showPicker) {
                  timeInput.showPicker();
                }
              }}
              onChange={handleTimeChange}
              className="border-gray-300 rounded-md w-full sm:w-auto"
              disabled={readOnly}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default memo(DateTimePickerInput);
